// Who drove, and in what, on earlier deliveries — the gate's memory of trucks.
//
//   useDriverNames()          every distinct driver name on the last 500
//                             deliveries, for Gate Intake's Driver Name
//                             suggestions (the berry board still has the same
//                             read inline, under the untenanted key).
//   useGrowerLastTruck(id)    THIS grower's most recent delivery that recorded
//                             a non-blank vehicle or driver. Gate Intake offers it as a
//                             "Last time: …" chip — offered, never filled
//                             without the operator's tap (F01 UF-07).
//
// Read-only. Both are keyed on the active tenant: deliveries are RLS-scoped,
// and a key without the tenant would serve the previous tenant's names for a
// beat after a switch.

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { latestRealTruck, type LastTruck } from '@/lib/gateIntakePrefill'


export function useDriverNames() {
  const tenantId = useActiveTenantId()
  return useQuery<string[]>({
    queryKey: ['delivery-drivers-history', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    // fallow-ignore-next-line complexity
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deliveries').select('driver_name')
        .not('driver_name', 'is', null)
        .is('deleted_at', null)
        .order('received_at', { ascending: false })
        .limit(500)
      if (error) throw error
      const set = new Set<string>()
      for (const r of (data ?? []) as { driver_name: string | null }[]) {
        if (r.driver_name?.trim()) set.add(r.driver_name.trim())
      }
      return Array.from(set).sort()
    },
  })
}

export function useGrowerLastTruck(growerId: string | null) {
  const tenantId = useActiveTenantId()
  return useQuery<LastTruck | null>({
    queryKey: ['grower-last-truck', tenantId, growerId],
    enabled: !!tenantId && !!growerId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deliveries').select('vehicle_reg, driver_name')
        .eq('grower_id', growerId!)
        .is('deleted_at', null)
        .or('vehicle_reg.not.is.null,driver_name.not.is.null')
        .order('received_at', { ascending: false })
        .limit(10)
      if (error) throw error
      // A reg/driver saved as '' passes the not-null filter; latestRealTruck
      // skips it so a blank latest row cannot hide an older real truck.
      return latestRealTruck(data as LastTruck[] | null)
    },
  })
}
